import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { AdminCard, FilterChip, ListState, StatusPill } from "@/components/admin/primitives";
import { formatDate } from "@/lib/dates";
import { formatBRL, padOrderNumber } from "@/lib/format";
import { useResellerOrders, type ResellerOrder } from "@/lib/resellers";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/revendedor/_area/vendas")({
  head: () => ({ meta: [{ title: "Vendas — Revenda MonteBox" }] }),
  component: ResellerSales,
});

type Filter = "todos" | "venda" | "em-aberto";

const badge = (order: ResellerOrder): { label: string; className: string } => {
  if (order.canceled)
    return { label: "Cancelado", className: "bg-destructive/10 text-destructive" };
  if (order.kind === "venda")
    return { label: "Venda", className: "bg-emerald-50 text-emerald-800" };
  return { label: "Em aberto", className: "bg-mb-surface text-mb-muted" };
};

/**
 * Vendas creditadas e pedidos em aberto — os que vieram pelo link e os dos
 * clientes da carteira que voltaram a comprar direto pelo site.
 *
 * "Em aberto" ainda não é venda: o cliente montou e enviou pelo site, e a
 * equipe confere, prepara a arte e recebe. Vira venda quando o pagamento é
 * confirmado. A etapa mostra onde o pedido está — o mesmo selo que o cliente
 * vê no painel dele.
 */
function ResellerSales() {
  const orders = useResellerOrders();
  const [filter, setFilter] = useState<Filter>("todos");
  const rows = (orders.data ?? []).filter((order) => filter === "todos" || order.kind === filter);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-mb-navy">Vendas</h1>
        <p className="mt-1 text-sm text-mb-muted">
          Vendas pagas dos seus clientes e pedidos em aberto do seu link e da sua carteira, com
          a etapa de cada um.
        </p>
      </div>

      <div className="flex gap-2">
        {(
          [
            ["todos", "Todos"],
            ["venda", "Vendas"],
            ["em-aberto", "Em aberto"],
          ] as const
        ).map(([value, label]) => (
          <FilterChip key={value} active={filter === value} onClick={() => setFilter(value)}>
            {label}
          </FilterChip>
        ))}
      </div>

      <AdminCard className="overflow-hidden">
        <ListState
          loading={orders.isLoading}
          error={orders.error}
          empty={rows.length === 0}
          emptyLabel="Nada por aqui ainda."
        />
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-mb-surface text-left text-[11px] font-bold uppercase tracking-[0.08em] text-mb-muted">
                <tr>
                  <th className="px-6 py-3">Pedido</th>
                  <th className="px-3 py-3">Cliente</th>
                  <th className="px-3 py-3">Peças</th>
                  <th className="px-3 py-3">Situação</th>
                  <th className="px-3 py-3">Etapa</th>
                  <th className="px-3 py-3 text-right">Valor</th>
                  <th className="px-6 py-3 text-right">Comissão</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {rows.map((order) => {
                  const tag = badge(order);
                  return (
                    <tr key={order.id} className="border-t border-mb-line align-top">
                      <td className="px-6 py-3">
                        <p className="font-display font-bold text-mb-navy">
                          #{padOrderNumber(order.number)}
                        </p>
                        <p className="text-xs text-mb-muted">
                          {formatDate(order.paidAt ?? order.createdAt)}
                        </p>
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-mb-ink">{order.customerName}</p>
                        <p className="text-xs text-mb-muted">{order.phone}</p>
                      </td>
                      <td className="max-w-[260px] px-3 py-3 text-xs text-mb-muted">
                        {order.summary}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={cn(
                            "rounded-full px-2.5 py-1 text-[11px] font-bold",
                            tag.className,
                          )}
                        >
                          {tag.label}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <StatusPill status={order.stage} className="text-[11px]" />
                      </td>
                      <td className="px-3 py-3 text-right text-mb-ink">{formatBRL(order.value)}</td>
                      <td className="px-6 py-3 text-right">
                        {order.commission === null ? (
                          <span className="text-xs text-mb-muted">quando pagar</span>
                        ) : (
                          <span
                            className={cn(
                              "font-semibold",
                              order.canceled ? "text-mb-muted" : "text-mb-ink",
                            )}
                          >
                            {formatBRL(order.commission)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>
    </div>
  );
}
